import { useState, useCallback, useMemo } from 'react';
import { getFrameForTime, getTimeForFrame } from '../utils/frameIndex';
import { parseJSONAsync } from '../utils/annotationUtils';
import { serializeRallies, deserializeRallies, incompleteRallyNumbers } from '../utils/rallyFile';
import { computeScoreboard } from '../utils/rallyScoring';

// Snap a raw playhead time onto the exact PTS of its frame so two labellers
// pausing on the same frame produce byte-identical times.
const snap = (time) => getTimeForFrame(getFrameForTime(time));

const byStartTime = (a, b) => a.startTime - b.startTime;

/**
 * Rally scoring state: the two player names, the list of scored rallies, and
 * the rally currently being timed.
 *
 * A rally is { id, startTime, endTime, winner } where winner is 'near' | 'far'
 * | null. Storing the side rather than the name means renaming a player fixes
 * every rally at once; see utils/rallyFile.js for the on-disk shape.
 *
 * The running point/game/set score is never stored: it is derived from the
 * winner sequence plus the first server, so editing an earlier rally rescores
 * everything after it automatically.
 */
export const useRallies = () => {
  const [nearPlayer, setNearPlayer] = useState('');
  const [farPlayer, setFarPlayer] = useState('');
  const [rallies, setRallies] = useState([]);
  // Who served the very first point; score derivation is impossible without it.
  const [initialServer, setInitialServer] = useState(null);
  const [adScoring, setAdScoring] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  // The rally awaiting a winner — the one just ended, so the 1/2 keys have a
  // target without the labeller having to click it first.
  const [awaitingWinnerId, setAwaitingWinnerId] = useState(null);

  const startRally = useCallback((time) => {
    setPendingStart(snap(time));
  }, []);

  const cancelPendingRally = useCallback(() => {
    setPendingStart(null);
  }, []);

  // Returns an error string when the rally could not be closed, null on success.
  const endRally = useCallback((time) => {
    if (pendingStart === null) return 'No rally in progress.';

    const endTime = snap(time);
    if (endTime <= pendingStart) {
      return 'Rally end must be after its start — scrub forward first.';
    }

    const id = `rally-${Date.now()}`;
    setRallies((prev) =>
      [...prev, { id, startTime: pendingStart, endTime, winner: null }].sort(byStartTime)
    );
    setPendingStart(null);
    setAwaitingWinnerId(id);
    return null;
  }, [pendingStart]);

  const setRallyWinner = useCallback((id, side) => {
    setRallies((prev) =>
      prev.map((rally) => (rally.id === id ? { ...rally, winner: side } : rally))
    );
    setAwaitingWinnerId((current) => (current === id ? null : current));
  }, []);

  // Move one edge of an existing rally to `time`, keeping start < end.
  const setRallyTime = useCallback((id, edge, time) => {
    const snapped = snap(time);
    setRallies((prev) =>
      prev
        .map((rally) => {
          if (rally.id !== id) return rally;
          if (edge === 'start') {
            return snapped < rally.endTime ? { ...rally, startTime: snapped } : rally;
          }
          return snapped > rally.startTime ? { ...rally, endTime: snapped } : rally;
        })
        .sort(byStartTime)
    );
  }, []);

  const deleteRally = useCallback((id) => {
    setRallies((prev) => prev.filter((rally) => rally.id !== id));
    setAwaitingWinnerId((current) => (current === id ? null : current));
  }, []);

  const incompleteRallies = useMemo(() => incompleteRallyNumbers(rallies), [rallies]);

  // Memoized so the rally rows do not rescore the match on every video frame.
  const scoreboard = useMemo(
    () => computeScoreboard(rallies, initialServer, adScoring),
    [rallies, initialServer, adScoring]
  );

  // Returns an error string when the file could not be written, null on success.
  const saveRalliesToJSON = useCallback((videoUrl) => {
    if (!nearPlayer.trim() || !farPlayer.trim()) {
      return 'Enter both player names before saving.';
    }
    if (initialServer === null) {
      return 'Pick the starting server before saving.';
    }
    if (rallies.length === 0) {
      return 'No rallies to save.';
    }
    // A rally with no winner is almost always an oversight rather than a
    // deliberate blank, so refuse the export and name the ones to revisit.
    const incomplete = incompleteRallyNumbers(rallies);
    if (incomplete.length > 0) {
      return `Pick a winner for rally ${incomplete.join(', ')} before saving.`;
    }

    const data = serializeRallies(nearPlayer.trim(), farPlayer.trim(), rallies, {
      initialServer,
      adScoring,
    });
    const blob = new Blob([JSON.stringify(data, null, 4)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    let videoFilename = '';
    try {
      if (videoUrl) {
        const lastPart = videoUrl.split('?')[0].split('/').pop();
        if (lastPart.toLowerCase().endsWith('.mp4')) videoFilename = lastPart;
      }
    } catch (error) {
      console.warn('Could not extract video filename from URL:', error);
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const a = document.createElement('a');
    a.href = url;
    a.download = videoFilename
      ? `${videoFilename}_rallies_${timestamp}.json`
      : `rallies_${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return null;
  }, [nearPlayer, farPlayer, rallies, initialServer, adScoring]);

  const loadRalliesFromFile = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const parsed = deserializeRallies(await parseJSONAsync(event.target.result));
          setNearPlayer(parsed.nearPlayer);
          setFarPlayer(parsed.farPlayer);
          setRallies(parsed.rallies);
          setInitialServer(parsed.initialServer);
          setAdScoring(parsed.adScoring);
          setPendingStart(null);
          setAwaitingWinnerId(null);
          resolve({ success: true, filename: file.name });
        } catch (error) {
          console.error('Error loading rallies:', error);
          resolve({ success: false, error: 'Invalid rally file format.' });
        }
      };
      reader.readAsText(file);
    });
  }, []);

  return {
    nearPlayer,
    setNearPlayer,
    farPlayer,
    setFarPlayer,
    initialServer,
    setInitialServer,
    adScoring,
    setAdScoring,
    rallies,
    scoreboard,
    pendingStart,
    awaitingWinnerId,
    incompleteRallies,
    startRally,
    endRally,
    cancelPendingRally,
    setRallyWinner,
    setRallyTime,
    deleteRally,
    saveRalliesToJSON,
    loadRalliesFromFile,
  };
};
